import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Quote, Sparkles, Trash2, Users, X } from "lucide-react";
import { api, apiError, logError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const seenKey = (date, uid) => `pramsia-quote-${uid}-${date}`;

export function DailyQuote() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [banner, setBanner] = useState(false);
  const [team, setTeam] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState({ text: "", author: "" });
  const [slot, setSlot] = useState(null);

  useEffect(() => {
    setSlot(document.getElementById("quote-banner-slot"));
  }, []);

  const loadExtras = useCallback(async () => {
    try {
      const [t, q] = await Promise.all([api.get("/team"), api.get("/quotes")]);
      setTeam(t.data);
      setQuotes(q.data);
    } catch (e) {
      logError("quote extras", e);
    }
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    api
      .get("/quote/today")
      .then(({ data: d }) => {
        setData(d);
        if (!localStorage.getItem(seenKey(d.date, user.id))) setBanner(true);
      })
      .catch((e) => logError("quote of the day", e));
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const ping = () => api.post("/presence/ping").catch(() => {});
    ping();
    const t = setInterval(ping, 90000);
    return () => clearInterval(t);
  }, [user?.id]);

  const dismissBanner = () => {
    setBanner(false);
    if (data && user?.id) localStorage.setItem(seenKey(data.date, user.id), "1");
  };

  useEffect(() => {
    if (open) loadExtras();
  }, [open, loadExtras]);

  const submit = async () => {
    try {
      await api.post("/quotes", form);
      toast.success("Added — it will show up on the team's rotation");
      setForm({ text: "", author: "" });
      loadExtras();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!data) return null;

  return (
    <>
      {banner && slot && createPortal(
        <div
          className="quote-banner flex items-start gap-3 border-b border-primary/30 bg-primary px-4 py-2.5 text-primary-foreground shadow-lg"
          data-testid="daily-quote-banner"
        >
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 opacity-80" />
          <button
            type="button"
            className="min-w-0 flex-1 text-left"
            data-testid="daily-quote-banner-open"
            onClick={() => {
              setOpen(true);
              dismissBanner();
            }}
          >
            <p className="truncate text-sm font-medium sm:text-base">“{data.quote.text}”</p>
            <p className="text-[11px] opacity-80">
              — {data.quote.author || "Unknown"} • thought of the day, tap to read more
            </p>
          </button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 text-primary-foreground hover:bg-white/20"
            data-testid="daily-quote-banner-close"
            aria-label="Dismiss thought of the day"
            onClick={dismissBanner}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>,
        slot
      )}

      <Button
        variant="ghost"
        size="icon"
        data-testid="daily-quote-button"
        aria-label="Thought of the day"
        onClick={() => setOpen(true)}
      >
        <Quote className="h-4 w-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="daily-quote-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" /> Thought of the day
            </DialogTitle>
            <DialogDescription>{data.date} • one thought for the whole team</DialogDescription>
          </DialogHeader>

          <blockquote
            className="rounded-lg border-l-4 border-primary bg-secondary p-4"
            data-testid="quote-text"
          >
            <p className="font-display text-lg leading-snug">“{data.quote.text}”</p>
            <footer className="mt-2 text-xs text-muted-foreground">
              — {data.quote.author || "Unknown"}
              {data.quote.added_by ? ` • shared by ${data.quote.added_by}` : ""}
            </footer>
          </blockquote>

          <Tabs defaultValue="add">
            <TabsList>
              <TabsTrigger value="add" data-testid="quote-tab-add">Add a thought</TabsTrigger>
              <TabsTrigger value="all" data-testid="quote-tab-all">
                Collection ({data.total_quotes})
              </TabsTrigger>
              <TabsTrigger value="team" data-testid="quote-tab-team">Who's here</TabsTrigger>
            </TabsList>

            <TabsContent value="add" className="space-y-2">
              <Textarea
                data-testid="quote-input"
                placeholder="Write something that motivates the team..."
                value={form.text}
                onChange={(e) => setForm({ ...form, text: e.target.value })}
              />
              <Input
                data-testid="quote-author-input"
                placeholder="Who said it (optional)"
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
              />
              <Button data-testid="quote-save-button" onClick={submit} disabled={form.text.trim().length < 8}>
                Share with the team
              </Button>
            </TabsContent>

            <TabsContent value="all">
              {quotes.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No team quotes yet — yours would be the first.
                </p>
              ) : (
                quotes.map((q) => (
                  <div key={q.id} className="flex items-start justify-between gap-2 border-b border-border py-2 text-xs last:border-0">
                    <span>
                      “{q.text}” <span className="text-muted-foreground">— {q.author || "Unknown"} • {q.added_by}</span>
                    </span>
                    {(q.added_by_id === user?.id || user?.role === "super_admin") && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6 shrink-0"
                        data-testid={`quote-delete-${q.id}`}
                        onClick={async () => {
                          try {
                            await api.delete(`/quotes/${q.id}`);
                            loadExtras();
                          } catch (e) {
                            toast.error(apiError(e));
                          }
                        }}
                      >
                        <Trash2 className="h-3 w-3 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="team" data-testid="team-directory">
              <div className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3.5 w-3.5" /> {team?.online || 0} online now • {team?.active || 0} active
                of {team?.total || 0} people
              </div>
              {(team?.members || []).map((m) => (
                <div key={m.id} className="flex items-center justify-between border-b border-border py-1.5 text-xs last:border-0">
                  <span>
                    {m.name}
                    <span className="ml-2 text-muted-foreground">{m.role_label}</span>
                  </span>
                  <span className="flex items-center gap-2 text-muted-foreground">
                    {m.online ? (
                      <span className="flex items-center gap-1 text-emerald-500" data-testid={`online-${m.id}`}>
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" /> online
                      </span>
                    ) : (
                      m.last_seen || "—"
                    )}
                    {!m.is_active && <Badge variant="outline" className="text-[9px]">inactive</Badge>}
                  </span>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
}
